import { Capacitor } from '@capacitor/core';
import {
  Appbar,
  AppBarVariant,
  Button,
  Checkbox,
  List,
  SearchWithSuggestions,
  Spacing,
  StyledDiv,
} from '@taleemabad/dsm/components';
import React, { useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';

export interface Contact {
  name: string;
  phone: string;
}

export interface UploadContactProps {
  contacts: Contact[];
  onUpload: (contacts: Contact[]) => void;
  onClose?: () => void;
}

export const UploadContact = ({
  contacts,
  onUpload,
  onClose,
}: UploadContactProps) => {
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const handleCheckboxChange = useCallback(
    (phone: string, isChecked: boolean) => {
      setSelectedPhones((prev) => {
        const updated = new Set(prev);
        isChecked ? updated.add(phone) : updated.delete(phone);
        return updated;
      });
    },
    [],
  );

  const filteredContacts = useMemo(() => {
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm),
    );
  }, [contacts, searchTerm]);

  const handleImport = useCallback(() => {
    const selected = contacts.filter((c) => selectedPhones.has(c.phone));
    const normalizedSelected = selected.map((c) => ({
      ...c,
      phone: normalizePhone(c.phone),
    }));
    onUpload(normalizedSelected);
  }, [contacts, selectedPhones, onUpload]);

  return (
    <StyledDiv
      flexDirection="column"
      padding="var(--rk-spacing-xl)"
      position="fixed"
      bottom="0"
      top="0px"
      left="0"
      background="--md-sys-color-surface-container-high"
      zIndex={1000}
    >
      <Appbar
        variant={AppBarVariant.Primary}
        title="Select Contacts"
        actions={[{ icon: 'close', onClick: onClose }]}
      />
      <SearchWithSuggestions
        onSearchChange={(val) => setSearchTerm(val)}
        placeholder="Search Contacts"
        showDropdownOnFocus={false}
        suggestions={[]}
      />

      <ContactListContainer
        height="calc(100vh - 250px)"
        flexDirection="column"
        gap={16}
        marginTop={16}
      >
        {filteredContacts.map((contact) => (
          <ContactListItem
            key={contact.phone}
            contact={contact}
            checked={selectedPhones.has(contact.phone)}
            onToggle={(isChecked) =>
              handleCheckboxChange(contact.phone, isChecked)
            }
          />
        ))}
      </ContactListContainer>

      <Spacing marginTop="xl">
        <Button
          variant="filled"
          onClick={handleImport}
          disabled={selectedPhones.size === 0}
        >
          Import Contacts ({selectedPhones.size})
        </Button>
      </Spacing>
    </StyledDiv>
  );
};

const ContactListItem = React.memo(
  ({
    contact,
    checked,
    onToggle,
  }: {
    contact: Contact;
    checked: boolean;
    onToggle: (isChecked: boolean) => void;
  }) => {
    return (
      <List
        listContainerStyle="transparent"
        alignment="center"
        divider={true}
        heading={contact.name}
        trailingComponent={
          <Checkbox
            onChange={(isChecked) => onToggle(isChecked)}
            checked={checked}
          />
        }
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.contact.phone === nextProps.contact.phone &&
    prevProps.checked === nextProps.checked,
);

const ContactListContainer = styled(StyledDiv)`
  overflow-y: ${() => (Capacitor.getPlatform() === 'ios' ? 'auto' : 'overlay')};
  background: var(--md-sys-color-surface-container-low);

  ::-webkit-scrollbar {
    width: var(--rk-spacing-lg);
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--md-sys-color-on-primary-container);
    border-radius: var(--rk-spacing-lg);
    border: var(--rk-spacing-xs) solid var(--md-sys-color-on-primary);
  }
`;

const normalizePhone = (phone: string) => {
  let cleaned = phone.replace(/-/g, '').replace(/\s+/g, '');
  cleaned = cleaned.replace(/^\+92/, '0');
  return cleaned;
};
